import React, { useState } from 'react';
import ImageUploader from '../common/ImageUploader';
import { scanRecipeCard } from '../../api/client';
import Modal from '../common/Modal';

export default function RecipeCardScanner({ isOpen, onClose, onRecipeExtracted }) {
  const [step, setStep] = useState('upload'); // 'upload', 'scanning', 'preview', 'error'
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);

  const handleUpload = (files) => {
    setUploadedFiles(files);
  };

  const handleScan = async () => {
    if (uploadedFiles.length === 0) {
      setError('Please upload at least one image');
      return;
    }

    setStep('scanning');
    setError(null);

    try {
      // Scan the first image (or combine if multiple)
      const file = uploadedFiles[0];
      const result = await scanRecipeCard(file.data);

      if (result.success) {
        setExtractedData(result.recipe_data);
        setStep('preview');
      } else {
        setError(result.error || 'Could not extract recipe from image');
        setStep('error');
      }
    } catch (err) {
      console.error('Scan error:', err);
      setError(err.message || 'Failed to scan recipe card');
      setStep('error');
    }
  };

  const handleUseRecipe = () => {
    // Pass extracted data to parent with the original images
    onRecipeExtracted({
      ...extractedData,
      sourceImages: uploadedFiles,
    });
    handleClose();
  };

  const handleClose = () => {
    setStep('upload');
    setUploadedFiles([]);
    setExtractedData(null);
    setError(null);
    onClose();
  };

  const handleRetry = () => {
    setStep('upload');
    setError(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Scan Recipe Card" size="xl">
      <div className="p-4">
        {step === 'upload' && (
          <>
            <p className="text-gray-600 mb-4">
              Take a photo or upload an image of your handwritten recipe card.
              For best results, ensure good lighting and that all text is visible.
            </p>

            <ImageUploader
              onUpload={handleUpload}
              onError={(err) => setError(err)}
              maxFiles={2}
              note="Upload front and back of recipe card"
              height={350}
            />

            {uploadedFiles.length > 0 && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 text-sm">
                  {uploadedFiles.length} image{uploadedFiles.length > 1 ? 's' : ''} ready to scan
                </p>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleScan}
                disabled={uploadedFiles.length === 0}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Scan Recipe
              </button>
            </div>
          </>
        )}

        {step === 'scanning' && (
          <div className="py-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent mb-4"></div>
            <p className="text-gray-600">Reading your recipe card...</p>
            <p className="text-gray-400 text-sm mt-2">This may take a few moments</p>
          </div>
        )}

        {step === 'preview' && extractedData && (
          <>
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm">
                Recipe extracted successfully! Review the details below.
              </p>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <p className="mt-1 text-lg font-semibold">{extractedData.title || 'Untitled'}</p>
              </div>

              {extractedData.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="mt-1 text-gray-600">{extractedData.description}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                {extractedData.servings && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Servings</label>
                    <p className="mt-1">{extractedData.servings}</p>
                  </div>
                )}
                {extractedData.prep_time_minutes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Prep Time</label>
                    <p className="mt-1">{extractedData.prep_time_minutes} min</p>
                  </div>
                )}
                {extractedData.cook_time_minutes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cook Time</label>
                    <p className="mt-1">{extractedData.cook_time_minutes} min</p>
                  </div>
                )}
              </div>

              {extractedData.ingredients?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ingredients ({extractedData.ingredients.length})
                  </label>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {extractedData.ingredients.map((ing, i) => (
                      <li key={i}>
                        {ing.quantity && `${ing.quantity} `}
                        {ing.unit && `${ing.unit} `}
                        {ing.name}
                        {ing.notes && ` (${ing.notes})`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {extractedData.instructions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instructions</label>
                  <p className="text-gray-600 whitespace-pre-wrap">{extractedData.instructions}</p>
                </div>
              )}

              {extractedData.notes && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <label className="block text-sm font-medium text-yellow-800">Notes from AI</label>
                  <p className="mt-1 text-yellow-700 text-sm">{extractedData.notes}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleRetry}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Try Again
              </button>
              <button
                type="button"
                onClick={handleUseRecipe}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Use This Recipe
              </button>
            </div>
          </>
        )}

        {step === 'error' && (
          <div className="py-8 text-center">
            <div className="inline-block p-3 bg-red-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-gray-800 font-medium mb-2">Could not extract recipe</p>
            <p className="text-gray-600 text-sm mb-6">{error}</p>

            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRetry}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
